'use client'

import { useEffect, useMemo } from 'react'
import { useUser } from '@auth0/nextjs-auth0'
import { buildRefinerTraits, identifyRefinerUser } from '@phoenix-web/react-shared/client-components'
import { identify } from '@/app/_wrappers/AnalyticsLoader'
import { appziIdentity } from '@/app/_wrappers/AppziLoader'
import { APP_ENV, APP_NAME } from '@/constants'
import { KnockUser } from '@/lib/auth-utils'
import type { RefinerConfig } from '@phoenix-web/react-shared/client-components'

/**
 * This component sets the analytics (Heap) identity on the client side, plus the feedback
 * vendors': Appzi's, and Refiner's (next to Heap) when Refiner is running.
 *
 * The Appzi identity is set either way: it only writes `window.appziSettings`, which
 * nothing reads unless `AppziLoader` rendered the script, and it goes away with Appzi.
 * Which vendor actually loads is `FeedbackLoader`'s decision alone.
 */

interface OnClientUserLoadProps {
  /**
   * Refiner config resolved by the layout via `getRefinerConfig`. Present only on
   * authenticated layouts, and only when Refiner can run, so its presence is the
   * whole condition for identifying the user in Refiner. It carries the person id
   * the signature was computed from, so the identify call cannot key on anything
   * else.
   */
  refiner?: RefinerConfig
}

export default function OnClientUserLoad({ refiner }: OnClientUserLoadProps) {
  const { user, isLoading } = useUser()
  const { account_id = null, roles = [] } = (user as KnockUser) || {}
  const refinerSignature = refiner?.signature
  // The person id and the signature both come from the server config, so the
  // signed value can never disagree with the identified one.
  const refinerPersonId = refiner?.personId
  const refinerEmail = refiner?.email

  // Set the default role based on the user's roles, to be used in appzi identity
  let defaultRole: string
  if ((roles as string[]).includes('lender')) {
    defaultRole = 'lender'
  } else if ((roles as string[]).includes('kca-agent')) {
    defaultRole = 'agent'
  }

  // `roles` is a new array on every render, so the memo is keyed on its stable
  // string equivalent rather than the array itself.
  const rolesKey = (roles as string[]).join(',')
  const refinerTraits = useMemo(
    () =>
      refinerPersonId
        ? buildRefinerTraits({
            personId: refinerPersonId,
            accountId: (account_id as string | number) ?? undefined,
            email: refinerEmail,
            roles: roles as string[],
            app: APP_NAME,
            environment: APP_ENV ?? 'unknown',
          })
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refinerPersonId, refinerEmail, account_id, rolesKey]
  )

  useEffect(() => {
    if (isLoading) return

    if (account_id) {
      appziIdentity(account_id as string, defaultRole)
      identify({ accountId: Number(account_id) })
    }
    // Keyed on the person id, not the account, so this does not wait on
    // `account_id`: Refiner surveys people with or without a Knock account.
    if (refinerTraits) identifyRefinerUser(refinerTraits, refinerSignature)
  }, [account_id, isLoading, refinerSignature, refinerTraits])

  return null
}
